import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlaidLinkOnSuccess, PlaidLinkOnEvent, PlaidLinkStableEvent, PlaidLinkOnEventMetadata } from 'react-plaid-link';
import CheckingAccountsList, {
  BankAccountType,
} from 'components/Verification/Steps/CheckingAccount/SelectAccount/components/BankAccountsList';
import { SavingsAccount } from 'api/PlaidApi';
import {
  exchangePublicToken,
  recommendGoal,
  updateAICoachGoals,
  getFinancialMetrics,
  getCreditScoreHistory,
  getGoalProgress,
  paraphraseNextStep,
  getSavingsAccounts,
} from 'thunks';
import { clearActiveWorkflow, consumePlaidSuccessCallback, setActiveWorkflow, WorkflowTypes } from 'handlers/aiChat';
import { addMessage, clearLastAction, updateLastMessage } from 'handlers/goalOptimizer';
import { PlaidEvent, usePlaidLinkSetup } from 'hooks/usePlaidLinkSetup';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getAIChat } from 'selectors/aiChat';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getBankAccount } from 'selectors/getBankAccount';
import { getGoalOptimizer } from 'selectors/goalOptimizer';
import { AICoachGoalPriority } from 'enums/AICoach';
import {
  getAICoachGoalTypeForDecisionType,
  resolveGoalFromAction,
  DebtPayoffDecisionTypes,
} from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/useGoal';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { getGoalDescription, AICoachActions, getParaphrasedSuccessMessage } from 'utils/aiCoachUtils';

import styles from './Plaid.module.scss';

interface PlaidWorkflowProps {
  onExit?: () => void;
  onError?: (error: any) => void;
}

const PlaidWorkflow = ({ onExit, onError }: PlaidWorkflowProps) => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { activeWorkflow } = useSelector(getAIChat);
  const { application } = useSelector(getApplicationData);
  const { savingsAccounts, isLoading: isLoadingSavingsAccounts } = useSelector(getBankAccount);
  const { aiCoachGoals, isLoading: isLoadingGoals } = useSelector(getLoanGoal);
  const {
    recommendation,
    educationActions,
    lastAction,
    isLoading: isLoadingParaphrase,
    recommendedPrimaryGoal,
  } = useSelector(getGoalOptimizer);
  const applicationId = application?.id;
  const isOfferAvailable = application?.status === ApplicationStatusName.OfferAvailable;
  const [showSavingsAccountSelection, setShowSavingsAccountSelection] = useState<boolean>(false);
  const hasSavingsAccounts = savingsAccounts && savingsAccounts.length > 0;

  useEffect(() => {
    if (!isLoadingSavingsAccounts) {
      dispatch(getSavingsAccounts(applicationId!));
    }
  }, []);

  const handlePlaidSuccess = useCallback(
    async (publicToken: string) => {
      if (!applicationId) return;

      try {
        setIsProcessing(true);

        // setPlaidPublicToken(publicToken);
        dispatch(setActiveWorkflow({ ...activeWorkflow!, openPlaid: false }));

        await dispatchWithUnwrap(exchangePublicToken({ applicationId, plaidPublicToken: publicToken }));

        // Only run recommendGoal if we don't have any messages yet (no recommendation has been shown)
        if (!recommendation) {
          await dispatchWithUnwrap(recommendGoal({ applicationId }));
          analytics.track('Recommended Action');
        }

        setIsProcessing(false);

        // Skip if fullScreen is true
        if (activeWorkflow?.fullScreen) return;

        const sourceActionId = activeWorkflow?.sourceActionId;

        // If this is for savings account selection (with only the Plaid popup), fetch savings accounts
        if (activeWorkflow?.fullScreen !== undefined && !activeWorkflow?.fullScreen) {
          dispatch(setActiveWorkflow({ ...activeWorkflow!, isLoading: false }));
        }

        dispatch(
          setActiveWorkflow({
            ...activeWorkflow!,
            success: true,
            isLoading: false,
          }),
        );

        analytics.track('AI Coach - Plaid Connected Successfully');

        if (typeof sourceActionId === 'number') {
          const callback = consumePlaidSuccessCallback(sourceActionId);
          if (callback) {
            try {
              const savingsAccountsResponse = await dispatchWithUnwrap(getSavingsAccounts(applicationId!));
              const newAccounts = savingsAccountsResponse?.savingsAccounts ?? [];

              if (newAccounts.length > 0) {
                await callback();
              } else {
                dispatch(clearActiveWorkflow());
              }
            } catch (error: unknown) {
              analytics.track('AI Coach - Plaid success callback failed', { error });
            }
          }
        }
      } catch (error: any) {
        setIsProcessing(false);
        analytics.track('AI Coach - Plaid Connection Failed', { error });
        onError?.(error);
      }
    },
    [
      applicationId,
      dispatch,
      dispatchWithUnwrap,
      onError,
      activeWorkflow?.sourceActionId,
      aiCoachGoals,
      recommendation,
    ],
  );

  const handleSuccess = useCallback<PlaidLinkOnSuccess>(
    async (publicToken: string) => {
      handlePlaidSuccess(publicToken);
    },
    [handlePlaidSuccess],
  );

  const handleEvent = useCallback<PlaidLinkOnEvent>(
    (evtName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => {
      analytics.track(`AI Coach - Plaid Event: ${evtName}`, { metadata });

      switch (evtName) {
        case PlaidEvent.BANK_INCOME_INSIGHTS_COMPLETED:
          break;
        case PlaidEvent.HANDOFF:
          break;
        default:
          break;
      }
    },
    [],
  );

  const handleExit = useCallback(() => {
    if (hasSavingsAccounts) return;
    dispatch(setActiveWorkflow({ success: false, type: WorkflowTypes.Plaid, isLoading: false }));
    onExit?.();
  }, [hasSavingsAccounts, dispatch, onExit]);

  const { open, ready, error } = usePlaidLinkSetup({
    onSuccess: handleSuccess,
    onEvent: handleEvent,
    onExit: handleExit,
    skipIncomeVerification: true,
  });

  // Auto-open Plaid when ready and workflow is loading
  useEffect(() => {
    if (ready && !isProcessing && activeWorkflow?.isLoading && activeWorkflow?.openPlaid) {
      open();
    }
  }, [ready, isProcessing, activeWorkflow, open]);

  useEffect(() => {
    if (!showSavingsAccountSelection && hasSavingsAccounts && activeWorkflow?.fullScreen) {
      setShowSavingsAccountSelection(true);
    }
  }, [showSavingsAccountSelection, savingsAccounts]);

  useEffect(() => {
    if (error) {
      analytics.track('AI Coach - Plaid Link Token Error', { error });
      onError?.(error);
    }
  }, [error, onError]);

  const handleSavingsAccountSelection = useCallback(
    async (selectedAccount: SavingsAccount) => {
      if (!applicationId || !lastAction) return;

      const savingsAccountId = selectedAccount.id;

      // Get the decision type from the lastAction that was clicked
      const actionGoals = lastAction.goals ?? [];

      if (actionGoals.length === 0) return;

      // Use centralized logic to resolve goal information
      const resolvedGoal = resolveGoalFromAction(actionGoals, aiCoachGoals, isOfferAvailable);

      if (!resolvedGoal) return;

      const { decisionType, goalToUpdate, shouldPreserveGoalType } = resolvedGoal;

      const goalType = getAICoachGoalTypeForDecisionType(decisionType);
      const latestGoals = aiCoachGoals ?? [];
      const existingGoal = latestGoals.find((g) => g.type === goalType);

      // If shouldPreserveGoalType is true (e.g., OTHER with SaveForSpecificExpense),
      // we update the existing goal with the decision type instead of creating a new goal
      const upsertGoal =
        shouldPreserveGoalType && goalToUpdate
          ? {
              ...goalToUpdate,
              type: goalToUpdate.type,
              decisionType,
              priority: AICoachGoalPriority.Primary,
              savingsAccountId,
              hasSavingsAccountId: true,
            }
          : {
              ...(existingGoal ?? { type: goalType }),
              type: goalType,
              decisionType,
              priority: AICoachGoalPriority.Primary,
              savingsAccountId,
              hasSavingsAccountId: true,
            };

      let nextGoals;
      if (shouldPreserveGoalType && goalToUpdate) {
        // Update the existing goal (e.g., OTHER) with the decision type and savings account
        nextGoals = latestGoals.map((g) => (g.type === goalToUpdate.type ? { ...g, ...upsertGoal } : g));
      } else {
        nextGoals = existingGoal
          ? latestGoals.map((g) => (g.type === goalType ? { ...g, ...upsertGoal } : g))
          : [...latestGoals, upsertGoal];
      }

      try {
        await dispatchWithUnwrap(updateAICoachGoals({ applicationId, goals: nextGoals }));
        analytics.track('Goal Confirmed', { goal: getGoalDescription(decisionType) });
        dispatch(getFinancialMetrics(applicationId));
        dispatch(getCreditScoreHistory(applicationId));
        if (application?.borrowerId) {
          dispatch(getGoalProgress(application.borrowerId));
        }

        // Update last message with success indicator
        const successMessage = getGoalDescription(decisionType);
        dispatch(updateLastMessage({ successMessage }));

        const successExplanation = await getParaphrasedSuccessMessage({
          applicationId,
          recommendedPrimaryGoal: recommendedPrimaryGoal ?? decisionType,
          dispatchWithUnwrap,
          paraphraseNextStep,
        });

        const shouldShowConsolidateButton = DebtPayoffDecisionTypes.includes(decisionType) && isOfferAvailable;
        const finalAction = shouldShowConsolidateButton
          ? AICoachActions.ConsolidateMyDebt
          : AICoachActions.CheckMyDashboard;

        dispatch(
          addMessage({
            explanation: successExplanation,
            actions: [...educationActions, finalAction],
          }),
        );

        setShowSavingsAccountSelection(false);
        dispatch(
          setActiveWorkflow({
            success: true,
            type: WorkflowTypes.Plaid,
            isLoading: false,
          }),
        );

        // Clear lastAction after successful update
        dispatch(clearLastAction());

        if (activeWorkflow?.sourceActionId) {
          const callback = consumePlaidSuccessCallback(activeWorkflow.sourceActionId);
          if (callback) {
            try {
              await callback();
            } catch (callbackError: unknown) {
              analytics.track('AI Coach - Plaid success callback failed', { error: callbackError });
            }
          }
        }
      } catch (updateError: unknown) {
        analytics.track('AI Coach - Failed to update goal with savings account', { error: updateError });
      }
    },
    [
      applicationId,
      activeWorkflow,
      aiCoachGoals,
      application?.borrowerId,
      educationActions,
      isOfferAvailable,
      lastAction,
      dispatch,
      dispatchWithUnwrap,
    ],
  );

  // Show savings account selection if available
  if (showSavingsAccountSelection && hasSavingsAccounts) {
    return (
      <div className={styles.accountSelectionContainer}>
        <h2>Select the account you want to use to track your goal</h2>
        <div className={styles.accountSelectionList}>
          <CheckingAccountsList
            onContinue={handleSavingsAccountSelection}
            buttonText="Next"
            accountType={BankAccountType.Savings}
            isLoading={isLoadingSavingsAccounts || isLoadingParaphrase || isLoadingGoals}
          />
        </div>
      </div>
    );
  }

  // Return empty while processing or waiting for Plaid link
  return <></>;
};

export default PlaidWorkflow;
